import { Box, Grid, Stack, Switch } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
  getCandidateDownloadTemplateById,
  IJobsDownloadTemplate,
  saveCandidateDownloadTemplaes,
} from 'wisestep-api';
import { WsButton, WsDialog, WsDrawer } from 'wisestep-base';

import { DrawerFooter } from '../../../../components/filters/components/AdvanceFilterFooterBar';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import FormSwitchLayout from '../../../careerPageSettings/components/formSwitchLayout';
import {
  defaultValues,
  fieldsAvailable,
  fieldsAvailableType,
  fieldsObject,
} from '../../../crmSettings/dataDownloadSettings/downloadSettings/helper';
import SaveTemplateDialog from '../../../jobs/listing/components/Download/SaveTemplateDialog';

export interface CreateExcelTemplateProps {
  openDialog: boolean;
  closeDialog(): void;
  variant: 'new' | 'edit' | 'clone';
  getLatestData: () => void;
  editOrCloneId: string;
}

const CreateCRMDownloadSettingsTemplate = ({
  openDialog,
  closeDialog,
  variant,
  getLatestData,
  editOrCloneId,
}: CreateExcelTemplateProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [fields, setFields] = useState(fieldsObject);
  const [downloadSettingsData, setDownloadSettingsData] =
    useState<IJobsDownloadTemplate>(defaultValues);
  const [open, setOpen] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>();

  const methods = useForm({ defaultValues: fields });
  const { setValue, control, reset, getValues } = methods;

  useEffect(() => {
    const fetchDataDownloadSettings = async () => {
      const getDataDownloadSettingsRes = await getCandidateDownloadTemplateById(editOrCloneId);
      variant === 'edit'
        ? setTemplateName(getDataDownloadSettingsRes.data.name)
        : setTemplateName('');
      if (getDataDownloadSettingsRes?.data.allowedColumns) {
        setDownloadSettingsData(getDataDownloadSettingsRes.data);
      } else {
        const tempData = getDataDownloadSettingsRes.data;
        tempData.allowedColumns = [];
        setDownloadSettingsData({ ...tempData });
      }
    };
    fetchDataDownloadSettings();
  }, [editOrCloneId, variant]);

  useEffect(() => {
    const json = fieldsAvailable.reduce((json, value) => {
      json[value] = downloadSettingsData?.allowedColumns.indexOf(value) != -1;
      return json;
    }, fieldsObject);
    if (json) setFields(json);
    reset(json);
  }, [downloadSettingsData?.allowedColumns, reset]);

  const saveTemplate = async (name: string) => {
    if (name) {
      const allowedColumns: string[] = [];
      const data = getValues();
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          allowedColumns.push(key);
        }
      });
      try {
        const tempData: IJobsDownloadTemplate = {
          id: variant === 'clone' ? '' : downloadSettingsData?.id,
          dataType: 'CRM',
          recruiterId: '',
          workspaceId: '',
          allowedColumns: allowedColumns ?? [],
          isAdmin: false,
          recordsDownloadLimit: 2000,
          downloadCcEmail: '',
          name: name ?? '',
          isHidden: false,
          isFavorite: false,
        };
        await saveCandidateDownloadTemplaes(tempData);
        closeDialog();
        getLatestData();
        setNotification(success('Attributes settings applied successfully'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    } else {
      setNotification(warning('Template Name is Mandatory please Enter'));
    }
  };

  return (
    <FormProvider {...methods}>
      <Box>
        <WsDrawer
          size="medium"
          open={openDialog}
          onClose={closeDialog}
          iconType="arrow"
          title={'Download Setting Template'}
        >
          <Grid container spacing={2} p={1}>
            {fields &&
              fieldsAvailable.map((e: fieldsAvailableType) => (
                <FormSwitchLayout
                  key={e}
                  label={e}
                  gridValue={5.8}
                  border="solid"
                  justifyContent="space-between"
                >
                  <Box pt={1}>
                    <Controller
                      name={e}
                      control={control}
                      render={(props) => (
                        <Switch
                          checked={props.field.value}
                          onChange={() => setValue(e, !props.field.value)}
                        />
                      )}
                    />
                  </Box>
                </FormSwitchLayout>
              ))}
          </Grid>
          <DrawerFooter>
            <Stack p={1} columnGap={2} direction="row" justifyContent="space-between">
              <Grid item container columnGap={1}>
                <WsButton variant="contained" size="large" onClick={() => setOpen(true)}>
                  Save Template
                </WsButton>
              </Grid>
            </Stack>
          </DrawerFooter>
        </WsDrawer>
      </Box>
      <WsDialog openDialog={open} onClose={() => setOpen(false)}>
        <SaveTemplateDialog
          getName={(name) => {
            saveTemplate(name);
            setOpen(false);
          }}
          close={() => setOpen(false)}
          templateName={templateName ?? ''}
        />
      </WsDialog>
    </FormProvider>
  );
};

export default CreateCRMDownloadSettingsTemplate;
